/**
 * Math subject focus API client — the student's own read/write of
 * `User.focusMathSubject` (`GET`/`PATCH /practice/focus-subject`).
 *
 * Focus, not filter. `/progress` has carried an ephemeral component-local
 * All/E-Math/A-Math toggle for a while; it changed what you browsed and
 * nothing about what got served. The durable value lives on the User row, so
 * "E-Math is over, only A-Math now" is true on every device and every surface.
 *
 * Self-only by construction: neither call takes a user id. Staff previewing a
 * cohort go through the "Try out as" selector, which is request-scoped and
 * never persisted, and staff viewing a named student's `/progress` keep a
 * local, ephemeral pick that never reaches this module.
 *
 * `null` is a value, not an absence — it means **Both**, i.e. today's
 * interleaved behaviour byte-for-byte. The PATCH body therefore always carries
 * the key.
 */
import type { MathSubject } from '@shared/mathSubjects';

import { fetchWithConfig } from './apiUtils';

export interface MathSubjectFocusResponse {
  /** The actor's stored focus. `null` = Both. */
  focusMathSubject: MathSubject | null;
  /** Enrolment truth (`User.mathSubjects`), verbatim — never derived. */
  mathSubjects: MathSubject[];
  /**
   * Whether the E-Math/A-Math split exists at all at this student's level —
   * the server's evaluation of `isMathSubjectApplicable(examLevel, orgLevel)`
   * against the actor's own profile. The split is real only in Sec 3/4 at
   * G2/G3; everywhere else Mathematics is one subject.
   */
  applicable: boolean;
}

export interface MathSubjectFocusUpdateResponse {
  focusMathSubject: MathSubject | null;
}

const ENDPOINT = '/practice/focus-subject';

export const mathSubjectFocusApi = {
  async get(token: string): Promise<MathSubjectFocusResponse> {
    return fetchWithConfig<MathSubjectFocusResponse>(ENDPOINT, {
      method: 'GET',
      authToken: token,
    });
  },

  /**
   * Writes the actor's own focus. `422 subject_not_enrolled` /
   * `422 subject_not_applicable` are the server re-checking the same two facts
   * the control gates on. The caller keeps its prior value authoritative until
   * this request succeeds; a rejection prompts a fresh read because the
   * student's enrolment or level may have changed.
   */
  async update(
    mathSubject: MathSubject | null,
    token: string
  ): Promise<MathSubjectFocusUpdateResponse> {
    return fetchWithConfig<MathSubjectFocusUpdateResponse>(ENDPOINT, {
      method: 'PATCH',
      authToken: token,
      body: JSON.stringify({ mathSubject }),
    });
  },
};
